import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const headerPadding = {
    paddingTop: isMobile ? '80px' : '3rem',
    paddingBottom: '3rem'
  };

  return (
    <>
      {/* Page Header */}
      <section className="section bg-theme-dark tc-light">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <div className="page-header" style={headerPadding}>
                <h1 className="title title-xl">Privacy Policy</h1>
                <p className="lead">Last Updated: February 16, 2026</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section bg-white">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">

              <h2 className="title mb-3">1. Introduction</h2>
              <p>Welcome to EBARANGAY.</p>
              <p>EBARANGAY is a government barangay management system operated by <strong>Digital Leader PH</strong>.</p>
              <p>EBARANGAY provides barangay services for Residents/Applicants (applying for barangay clearances, business permits, pet registration, filing incident reports, viewing events and ordinances, accessing barangay hotline contacts, and participating in amicable settlements) and Barangay Officials (managing and processing barangay applications, incident reports, events, ordinances, in-app messaging, kiosk mode applicant management, and other barangay services).</p>
              <p>This Privacy Policy describes how <strong>Digital Leader PH</strong> collects, uses, and discloses information when you use our services through the EBARANGAY mobile application.</p>
              <p>By using EBARANGAY, you agree to this Privacy Policy and our <Link to="/terms-and-conditions">Terms and Conditions</Link>.</p>

              <h2 className="title mb-3 mt-5">2. Defined Terms</h2>
              <p>The term "SERVICE" refers to the EBARANGAY mobile application, which is owned and maintained by Digital Leader PH.</p>
              <p>PERSONAL DATA refers to information on a living human who can be identified based on the information (or from those and other information either in our possession or likely to come into our possession).</p>
              <p>USAGE DATA is data that is gathered automatically as a result of the use of the Service or as a result of the Service infrastructure itself (for example, the duration of app usage).</p>
              <p>COOKIES are little files that your device stores on it (mobile device).</p>
              <p>DATA CONTROLLER is a natural or legal person who, alone or jointly or in concert with other persons, determines the purposes and manner in which personal data are or will be processed. We are the Data Controller of your data for this Privacy Policy.</p>
              <p>DATA PROCESSORS (OR SERVICE PROVIDERS) refers to any natural or legal person who processes data on the Data Controller's behalf. We may employ the services of a variety of Service Providers to more efficiently process your data.</p>
              <p>ANY LIVING INDIVIDUAL WHO IS THE SUBJECT OF PERSONAL DATA IS THE DATA SUBJECT.</p>
              <p>THE USER is the entity that makes use of our Service. The User is the Data Subject, the individual who is the subject of Personal Data.</p>

              <h2 className="title mb-3 mt-5">3. Information Collection and Utilization</h2>
              <p>We collect a variety of different sorts of information to provide and improve our Service as a government barangay management system.</p>

              <h2 className="title mb-3 mt-5">4. Data Types Collected</h2>
              <p><strong>Individual Data</strong></p>
              <p>During your use of our Service, we may request that you provide us with some personally identifiable information that can be used to contact or identify you ("Personal Data"). Among the types of personally identifiable information that may be collected are, but are not limited to:</p>
              <ul>
                <li>Electronic mail address</li>
                <li>First name, middle name, and last name</li>
                <li>Contact number (mobile and landline)</li>
                <li>Complete address (street, barangay, city, province, postal code)</li>
                <li>Date of birth and age</li>
                <li>Place of birth</li>
                <li>Gender (male, female)</li>
                <li>Civil status (single, married, widowed, separated, divorced)</li>
                <li>Nationality</li>
                <li>Religion</li>
                <li>Occupation and monthly income</li>
                <li>Blood type</li>
                <li>Nickname</li>
                <li>Sector classification (senior citizen, youth, PWD, solo parent)</li>
                <li>Residency information (ownership type: owned, rented, caretaker)</li>
                <li>Household information (number of households, living in disaster-prone area)</li>
                <li>Precinct number and PhilHealth number</li>
                <li>Tricycle ownership and plate number (if applicable)</li>
                <li>Reference person information (name, relationship, contact number)</li>
                <li>Spouse name (if married)</li>
              </ul>
              <p>We may contact you using your Data to send you notifications about your barangay service applications, processing updates, and other information related to your barangay services. You may manage your notification preferences within the app settings or by emailing <a href="mailto:support@ebarangay.ph">support@ebarangay.ph</a>.</p>

              <p><strong>Applicant Information (For Residents):</strong></p>
              <p>When applying for barangay services, we collect:</p>
              <ul>
                <li>Personal identification details as listed above</li>
                <li>Application purpose and details</li>
                <li>Supporting documents and identification</li>
                <li>Application status and processing history</li>
                <li>Approval/rejection information and remarks</li>
              </ul>
              <p>This applicant data is treated as Personal Data and subject to all protections in this Privacy Policy.</p>

              <p><strong>Barangay Service-Specific Data:</strong></p>

              <p><strong>Barangay Clearances:</strong></p>
              <ul>
                <li>Type of clearance (employment, business, scholarship, travel)</li>
                <li>Purpose of clearance</li>
                <li>Issued date and validity period</li>
                <li>Official Receipt (OR) number and amount paid</li>
                <li>Processing and approval history</li>
              </ul>

              <p><strong>Business Permits:</strong></p>
              <ul>
                <li>Business name, address, and contact information</li>
                <li>Nature of business</li>
                <li>Owner name and address</li>
                <li>Contact person details</li>
                <li>Permit type (new, renewal, temporary)</li>
                <li>Validity dates</li>
                <li>OR number and amount paid</li>
              </ul>

              <p><strong>Blotter Records:</strong></p>
              <ul>
                <li>Complaint title and details</li>
                <li>Complaint date and location</li>
                <li>Person to complain against</li>
                <li>Witnesses</li>
                <li>Action taken and resolution</li>
                <li>Mediation details and hearing dates</li>
              </ul>

              <p><strong>Pet Registrations:</strong></p>
              <ul>
                <li>Pet name, species, breed, color, sex, age, date of birth</li>
                <li>Vaccination status and dates</li>
                <li>Vaccination certificate number</li>
                <li>Veterinarian name</li>
              </ul>

              <p><strong>Amicable Settlements:</strong></p>
              <ul>
                <li>Settlement title and details</li>
                <li>Settlement date and type</li>
                <li>Parties involved (complainant and respondent)</li>
                <li>Mediator information</li>
                <li>Signed agreements</li>
                <li>Witness information</li>
              </ul>

              <p><strong>Incident Reports:</strong></p>
              <ul>
                <li>Incident type (SOS, medical, fire, crime, accident, missing person, suspicious activity, public disturbance, other)</li>
                <li>Severity level (low, medium, high, critical)</li>
                <li>Date and time reported</li>
                <li>Source of report (onsite or mobile)</li>
                <li>GPS tracker link (if applicable, for location-based dispatch)</li>
                <li>Assigned personnel information</li>
                <li>Dispatch, response, and resolution timestamps</li>
                <li>Incident status (reported, dispatched, in progress, resolved, cancelled, archived)</li>
                <li>Remarks and resolution details</li>
                <li>Incident number for tracking</li>
              </ul>

              <p><strong>Events Data:</strong></p>
              <ul>
                <li>Event title, description, and category</li>
                <li>Event dates (start date, end date)</li>
                <li>Event location</li>
                <li>Organizer information</li>
                <li>Event status</li>
              </ul>

              <p><strong>Ordinances Data:</strong></p>
              <ul>
                <li>Ordinance number and title</li>
                <li>Description and full text</li>
                <li>Enacted date and effective date</li>
                <li>Status (active, repealed, amended)</li>
                <li>Category and author</li>
                <li>Applicable penalties (if any)</li>
              </ul>

              <p><strong>IP-Based Location Detection:</strong></p>
              <p>To provide location-aware services (such as auto-detecting your country and region for address forms), we may use third-party IP geolocation services. These services receive your IP address and return approximate geographic information:</p>
              <ul>
                <li>Services used: ip-api.com, ipapi.co, ipinfo.io</li>
                <li>Data sent: Your IP address (automatically included in the request)</li>
                <li>Data received: Approximate country, region, city, and timezone</li>
                <li>Purpose: To pre-fill address forms and provide region-appropriate content</li>
                <li>We do not store your IP address separately; this data is used transiently during your session</li>
                <li>These services have their own privacy policies governing how they handle IP data</li>
              </ul>

              <p><strong>Authentication Data:</strong></p>
              <ul>
                <li>OAuth tokens (for Google Sign-In)</li>
                <li>One-Time Passwords (OTPs) sent via email for verification (encrypted via AES before transmission)</li>
                <li>CSRF (Cross-Site Request Forgery) tokens for session security</li>
                <li>Session tokens and authentication state</li>
                <li>Login timestamps and device information</li>
              </ul>

              <p><strong>Applicant Management Data (Kiosk Mode):</strong></p>
              <p>When applicant records are created via kiosk mode by barangay personnel, the following data is collected:</p>
              <ul>
                <li>All personal identification details as listed in the Individual Data section above</li>
                <li>The kiosk account and operator who created the record</li>
                <li>Timestamp of record creation</li>
                <li>This data is subject to the same protections and retention policies as all other applicant data</li>
              </ul>

              <p><strong>Usage Data</strong></p>
              <p>Additionally, we may collect information sent by your mobile device whenever you use our Service ("Usage Data").</p>
              <p>This Usage Data may comprise the device identifier of your mobile device, the device model and operating system version, the pages/screens of our Service that you visit, the time and date of your visit, the time spent on those pages/screens, unique device identifiers and other diagnostic data.</p>
              <p>When you use a mobile device to access the Service, this Usage Data may contain information on the type of device you are using, its unique identifier, its operating system version, and other diagnostic data.</p>

              <p><strong>Location Data (For GPS Tracking)</strong></p>
              <p>For authorized barangay personnel (Tanod, GPS operators), we collect and retain real-time location information ("Location Data") for security, patrol monitoring, and emergency response purposes. We use this data to:</p>
              <ul>
                <li>Track real-time location of barangay personnel during duty hours</li>
                <li>Monitor patrol routes and coverage areas</li>
                <li>Respond to emergency situations</li>
                <li>Generate tracking reports and audit trails</li>
              </ul>
              <p>Location data collected includes:</p>
              <ul>
                <li>Latitude and longitude coordinates (GPS)</li>
                <li>Accuracy of GPS signal (in meters)</li>
                <li>Speed and direction of travel</li>
                <li>Timestamp of location updates</li>
                <li>Battery level of tracking device</li>
                <li>GPS source type (mobile GPS or tracker GPS)</li>
              </ul>
              <p>GPS tracking is enabled only for authorized personnel and only during official duty hours. Personnel can view their own tracking history through the app.</p>
              <p>You can activate or disable location services while using our Service at any time by adjusting the settings on your device. However, disabling GPS tracking may prevent barangay personnel from using tracking features required for their duties.</p>

              <p><strong>Cookie Data Monitoring</strong></p>
              <p>We track user behavior on our Service using cookies and similar tracking technologies and save certain information.</p>
              <p>Cookies are little data files that may contain an anonymous unique identifier. Cookies are little text files that are stored on your mobile device. Additional tracking technologies are also used to collect and track information, as well as to develop and analyze our Service.</p>
              <p>You may configure your device settings to refuse all cookies or to notify you when a cookie is transmitted. However, if you disable cookies, you may be unable to use certain features of our Service.</p>
              <p>Cookies that we utilize include the following:</p>
              <ul>
                <li><strong>Session Cookies:</strong> To operate our Service, we employ Session Cookies.</li>
                <li><strong>Preference Cookies:</strong> Preference Cookies are used to store information about your preferences and other settings.</li>
                <li><strong>Security Cookies:</strong> We utilize Security Cookies to ensure your security.</li>
                <li><strong>Analytics Cookies:</strong> Analytics Cookies are used to analyze app usage and improve service delivery.</li>
              </ul>

              <h2 className="title mb-3 mt-5">5. Data Utilization</h2>
              <p>Digital Leader PH collects data for a variety of purposes:</p>
              <ul>
                <li>To provide and maintain our barangay services;</li>
                <li>To keep you informed of changes to our Service and application status updates;</li>
                <li>To enable you to use barangay services (clearances, permits, incident reports, pet registration, settlements, events, ordinances, hotline);</li>
                <li>To provide customer support for barangay service applications;</li>
                <li>To conduct analysis or collect valuable information to improve our Service;</li>
                <li>To track how our Service is used for service improvement;</li>
                <li>To identify, avoid, and resolve technical problems;</li>
                <li>To process barangay service applications (clearances, permits, incident reports, settlements);</li>
                <li>To maintain government records in compliance with Philippine law;</li>
                <li>To send you notifications regarding your applications, processing status, and approvals;</li>
                <li>To facilitate GPS tracking for authorized barangay personnel for security and emergency response;</li>
                <li>To generate reports and analytics for barangay management;</li>
                <li>To facilitate in-app messaging between barangay personnel;</li>
                <li>To manage applicant records via kiosk mode;</li>
                <li>With your consent, for any other purpose related to barangay services.</li>
              </ul>

              <h2 className="title mb-3 mt-5">6. Data Retention</h2>
              <p>We will retain your data for as long as necessary to fulfill the purposes outlined in this Privacy policy and to comply with Philippine law for government record-keeping.</p>
              <p>As a government service, we are required to retain certain records for legal and regulatory compliance:</p>

              <p><strong>Government Records (10 years minimum):</strong></p>
              <ul>
                <li>Barangay clearance records: Retained for 10 years after issuance</li>
                <li>Business permit records: Retained for 10 years after expiry</li>
                <li>Incident report records: Retained for 10 years after filing</li>
                <li>Blotter records: Retained for 10 years after filing</li>
                <li>Pet registration records: Retained for 10 years after registration</li>
                <li>Amicable settlement records: Retained for 10 years after settlement</li>
                <li>Applicant profile data: Retained for 10 years or as required by law</li>
              </ul>

              <p><strong>Other Records:</strong></p>
              <ul>
                <li>Event records: Retained for 5 years after the event date</li>
                <li>Ordinance records: Retained permanently or as required by law</li>
                <li>In-app messages: Retained for 5 years for audit and accountability purposes</li>
                <li>Audit logs: Retained for 5 years for security and compliance purposes</li>
              </ul>

              <p><strong>User Profile Data:</strong></p>
              <ul>
                <li>Active user accounts: Retained while account is active</li>
                <li>Deleted accounts: Personal information removed within 30 days of deletion request</li>
                <li>Government records remain stored even after account deletion (as required by Philippine Data Privacy Act)</li>
              </ul>

              <p><strong>GPS Tracking Data:</strong></p>
              <ul>
                <li>Active tracking data: Retained for real-time monitoring</li>
                <li>Historical tracking data: Retained for 90 days for review and auditing</li>
                <li>Extended retention: May be retained longer if required for investigations or legal proceedings</li>
              </ul>

              <p><strong>Authentication Data:</strong></p>
              <ul>
                <li>OTP codes: Deleted upon verification or expiration (typically within 10 minutes)</li>
                <li>Session tokens: Deleted upon logout or expiration</li>
                <li>CSRF tokens: Deleted upon session end</li>
                <li>OAuth tokens: Retained while account is active; revoked upon account deletion</li>
              </ul>

              <p><strong>Usage Data:</strong></p>
              <ul>
                <li>Usage Data is normally stored for a shorter amount of time for analytics and service improvement</li>
                <li>May be retained longer if used to increase security or operation of our Service</li>
              </ul>

              <h2 className="title mb-3 mt-5">7. Data Transfer</h2>
              <p>Your information, including Personal Data, may be transferred to - and retained on - servers located outside of your barangay, city, province, or other governmental jurisdiction, where data protection regulations may differ.</p>
              <p>If you are located outside the Philippines and prefer to provide us your data, please be aware that we transfer and process data, including Personal Data, in the Philippines.</p>
              <p>Your agreement of this Privacy policy, followed by the submission of such information, constitutes your permission to that transfer.</p>
              <p>Digital Leader PH will take all reasonable steps to ensure that your data is treated securely and in accordance with this Privacy Policy, and no transfer of your Personal Data to an organization or country will occur unless and until adequate controls, including those relating to the security of your data and other personal information, are in place.</p>

              <h2 className="title mb-3 mt-5">8. Data Disclosure</h2>
              <p>We may disclose personal information that we collect or that you supply to the following entities:</p>
              <p><strong>Law Enforcement Disclosure.</strong></p>
              <p>We may be forced to disclose your Personal Data in some circumstances, such as where compelled by law or in response to authorized requests by public authorities, law enforcement agencies, or government bodies.</p>
              <p><strong>Government Agencies.</strong></p>
              <p>As a government service, we may share barangay records with authorized government agencies, including but not limited to:</p>
              <ul>
                <li>Department of Interior and Local Government (DILG)</li>
                <li>National Privacy Commission (NPC)</li>
                <li>Philippine Statistics Authority (PSA)</li>
                <li>Other government agencies as required by law</li>
              </ul>
              <p><strong>Barangay Officials.</strong></p>
              <p>Personal Data is accessible to authorized barangay officials within your barangay jurisdiction for the purpose of processing applications and providing barangay services. Access is limited based on user type (admin, captain, secretary, treasurer, staff, etc.).</p>
              <p><strong>Commercial Transaction.</strong></p>
              <p>Your Personal Data may be transferred if we or our parent company are involved in a merger, acquisition, or asset sale.</p>
              <p><strong>Additional instances.</strong> Additionally, we may disclose your information as follows:</p>
              <ul>
                <li>To our subsidiaries and affiliates;</li>
                <li>To contractors, service providers, and other third-party service providers who assist us in providing barangay services (e.g., cloud hosting providers);</li>
                <li>To accomplish the purpose for which it was provided;</li>
                <li>For any other purpose that we disclose to you when you supply us with the information;</li>
                <li>In all other instances, with your approval;</li>
                <li>If we believe disclosure is required or appropriate to protect the rights, property, or safety of the barangay, residents, or others.</li>
              </ul>

              <h2 className="title mb-3 mt-5">9. Data Security</h2>
              <p>While we value the security of your data, keep in mind that no method of transmission over the Internet or technique of electronic storage is completely safe. While we make every effort to protect your Data in a commercially reasonable manner, we cannot guarantee its perfect security.</p>
              <p><strong>Security Measures We Implement:</strong></p>
              <ul>
                <li>Data encryption in transit (HTTPS/TLS)</li>
                <li>Data encryption at rest (AES-256)</li>
                <li>AES encryption for sensitive local data (including OTP codes before email transmission)</li>
                <li>Row-Level Security (RLS) policies on database</li>
                <li>CSRF (Cross-Site Request Forgery) token protection for all authenticated requests</li>
                <li>Rate limiting (5 requests per minute) to prevent abuse and brute-force attacks</li>
                <li>Input sanitization and XSS (Cross-Site Scripting) prevention on all user inputs</li>
                <li>File upload validation (file type whitelist, MIME type verification, file size limits)</li>
                <li>File name sanitization to prevent path traversal and injection attacks</li>
                <li>Multi-factor authentication for barangay officials (where applicable)</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls based on user roles</li>
                <li>Comprehensive audit logging of all data access, modifications, and security events</li>
              </ul>
              <p><strong>Multi-User Data Separation:</strong></p>
              <ul>
                <li>All data for different barangays is logically separated in the database</li>
                <li>Barangay officials can only access data within their barangay jurisdiction</li>
                <li>Row-Level Security ensures data isolation between barangays</li>
                <li>Applicants can only access their own applications and records</li>
                <li>Access controls prevent unauthorized data access</li>
              </ul>

              <h2 className="title mb-3 mt-5">10. Your Rights Regarding Data Protection In accordance with the General Data Protection Regulation (GDPR)</h2>
              <p>If you live in the European Union (EU) or the European Economic Area (EEA), you have certain data protection rights under the GDPR.</p>
              <p>We will make commercially reasonable efforts to ensure that your Personal Data is accurate, complete, and up to date.</p>
              <p>If you would like to know what Personal Data we possess on you and request that it be deleted from our systems, please contact us via email at <a href="mailto:support@ebarangay.ph">support@ebarangay.ph</a>.</p>
              <p>You have the following data protection rights in certain circumstances:</p>
              <ul>
                <li>The right to request access to, update, or deletion of the information we maintain about you (subject to government record retention requirements);</li>
                <li>The rectification right. You have the right to have erroneous or incomplete information about you updated;</li>
                <li>The right to object. You have the right to object to the way your Personal Data is used by us;</li>
                <li>The limit right. You have the right to request a restriction on how your personal information is processed;</li>
                <li>The right to portability of data. You have the right to obtain a copy of your Personal Data in a structured, machine-readable, and frequently used format.</li>
                <li>The right to revoke consent. Additionally, you have the right to withdraw your consent at any time for us to handle your personal information based on your consent;</li>
              </ul>
              <p>Please be aware that we may require verification of your identity before responding to such inquiries. Please keep in mind that we may be unable to delete certain government records that are required to be retained by Philippine law.</p>
              <p>You have the right to lodge a complaint with a Data Protection Authority regarding how we collect and use your Personal Data. For additional information, please contact your European Economic Area's data protection authorities (EEA).</p>

              <h2 className="title mb-3 mt-5">11. Your Data Protection Rights under the California Privacy Protection Act (CalOPPA)</h2>
              <p>CalOPPA is the nation's first state law requiring commercial websites and online services to disclose a privacy policy. The law's reach extends well beyond California, requiring any person or business in the United States (and conceivably the world) that operates applications that collect personally identifiable information from California residents to prominently display a privacy policy outlining the information being collected and the individuals with whom it will be shared, as well as to adhere to this statement.</p>
              <p>CalOPPA implies that we agree with the following:</p>
              <ul>
                <li>Users can use basic features of our app;</li>
                <li>Our Privacy Policy is accessible from our app and website;</li>
                <li>Any modifications to our privacy policies will be communicated to users via our Privacy Policy and in-app notifications;</li>
                <li>Users may update their personal information at any time by emailing us at <a href="mailto:support@ebarangay.ph">support@ebarangay.ph</a>.</li>
              </ul>
              <p><strong>Our Policy Regarding "Do Not Track" (DNT) Signals:</strong></p>
              <p>When a "Do Not Track" browser/device feature is enabled, we respect Do Not Track signals and do not track, plant cookies beyond essential functionality, or employ advertising. Do Not Track is a feature that you can enable to alert applications that you do not wish to be monitored.</p>
              <p>You can enable or disable Do Not Track by accessing your device's Settings.</p>

              <h2 className="title mb-3 mt-5">12. California Consumer Privacy Act (CCPA): Your Data Protection Rights</h2>
              <p>If you live in California, you have the right to request information about the data we collect about you, to have it deleted, and to have it not sold (shared). To exercise your data protection rights, you may submit certain requests and inquire about the following:</p>
              <p><strong>What personally identifiable information we hold about you.</strong> If you submit this request, we will respond as follows:</p>
              <ul>
                <li>The types of personal information about you that we have gathered.</li>
                <li>The sources from which we collect your personally identifiable information.</li>
                <li>The purpose for which your personal information is collected (barangay services).</li>
                <li>The types of third parties with whom we disclose personally identifiable information.</li>
                <li>The exact pieces of personal information about you that we have gathered.</li>
                <li>We do not sell your personal information. We will notify you of this.</li>
                <li>A list of the categories of personal information we have disclosed for government purposes.</li>
              </ul>
              <p>Please note that you have the right to request this information from us up to two times per rolling twelve-month period. When you submit your request, the information you receive may be limited to the personal information we have gathered about you in the preceding 12 months.</p>
              <p><strong>To ensure that your personal information is deleted.</strong> If you make this request, we will erase all personal information about you that we hold from our records, except where retention is required by Philippine law for government record-keeping (minimum 10 years for barangay records). In some instances, deletion may be performed by de-identifying the data.</p>
              <p><strong>To discontinue the sale of your personal information.</strong> We do not sell or rent your personally identifiable information to third parties for any purpose. We will never sell your personal information for financial gain.</p>
              <p>We will cease any data sharing if you submit a request to do so (except where required by law).</p>
              <p>Please keep in mind that if you request that we delete your data, certain government records may still be retained as required by Philippine law, but your personal account information will be deleted.</p>
              <p>To exercise your California data protection rights as outlined above, please send an email to <a href="mailto:support@ebarangay.ph">support@ebarangay.ph</a> with your request(s).</p>
              <p>The CCPA, abbreviated for the California Consumer Privacy Act, protects the data protection rights outlined above. To learn more, visit the official website of the California Legislative Information. The CCPA became effective on January 1, 2020.</p>

              <h2 className="title mb-3 mt-5">13. Philippine Data Privacy Act (DPA) Compliance</h2>
              <p>EBARANGAY fully complies with the Data Privacy Act of 2012 (Republic Act No. 10173) and regulations issued by the National Privacy Commission (NPC).</p>
              <p><strong>Rights under the Philippine Data Privacy Act:</strong></p>
              <p><strong>a) Right to be Informed</strong></p>
              <ul>
                <li>You have the right to know how your personal data is collected, used, and shared</li>
                <li>This Privacy Policy serves as our primary disclosure mechanism</li>
              </ul>
              <p><strong>b) Right to Access</strong></p>
              <ul>
                <li>You have the right to access your personal data in our systems</li>
                <li>You may request copies of your barangay applications and records</li>
                <li>Access requests must be submitted to <a href="mailto:support@ebarangay.ph">support@ebarangay.ph</a></li>
              </ul>
              <p><strong>c) Right to Object</strong></p>
              <ul>
                <li>You may object to the processing of your personal data for certain purposes</li>
                <li>However, processing required for government services may continue despite objection</li>
              </ul>
              <p><strong>d) Right to Erasure or Blocking</strong></p>
              <ul>
                <li>You may request deletion of your personal data (subject to government retention requirements)</li>
                <li>Government records (clearances, permits, blotters) must be retained for 10 years per law</li>
                <li>Personal account data can be deleted, but application records remain as government documents</li>
              </ul>
              <p><strong>e) Right to Damages</strong></p>
              <ul>
                <li>You have the right to claim damages for violations of your data privacy rights</li>
                <li>Claims may be filed with the National Privacy Commission</li>
              </ul>
              <p><strong>f) Right to Data Portability</strong></p>
              <ul>
                <li>You may request your personal data in a structured, machine-readable format</li>
                <li>Data export requests must be submitted to <a href="mailto:support@ebarangay.ph">support@ebarangay.ph</a></li>
                <li>Processing time: 30 days from request</li>
              </ul>
              <p><strong>g) Right to File a Complaint</strong></p>
              <ul>
                <li>You may file complaints with the National Privacy Commission</li>
                <li>Contact NPC: <a href="mailto:privacy@privacy.gov.ph">privacy@privacy.gov.ph</a> or visit <a href="https://www.privacy.gov.ph" target="_blank" rel="noopener noreferrer">www.privacy.gov.ph</a></li>
              </ul>
              <p><strong>Legal Basis for Processing:</strong></p>
              <ul>
                <li><strong>Consent:</strong> By using EBARANGAY, you consent to the collection and processing of your data</li>
                <li><strong>Legal Obligation:</strong> We process data to comply with government record-keeping requirements</li>
                <li><strong>Public Interest:</strong> We process data to provide barangay services for the public benefit</li>
                <li><strong>Legitimate Interest:</strong> We process data to improve barangay service delivery</li>
              </ul>

              <h2 className="title mb-3 mt-5">14. Phone Contacts Access</h2>
              <p><strong>This feature is optional and user-initiated.</strong></p>
              <p>EBARANGAY may request permission to access your device's phone contacts to simplify the process of adding contact information for applicants and reference persons.</p>
              <p><strong>What we collect from your contacts:</strong></p>
              <ul>
                <li>Contact names (first and last name)</li>
                <li>Phone numbers</li>
                <li>Email addresses</li>
              </ul>
              <p><strong>How we use contacts data:</strong></p>
              <ul>
                <li>To auto-fill contact information forms when you choose to import a contact</li>
                <li>To simplify data entry and reduce errors when creating applicant records</li>
                <li>Contacts are accessed only when you explicitly choose to import a contact</li>
              </ul>
              <p><strong>What we DON'T do with your contacts:</strong></p>
              <ul>
                <li>We do NOT automatically upload or store your entire contacts list</li>
                <li>We do NOT share, sell, or transfer contact information to third parties</li>
                <li>We do NOT use contacts for marketing or advertising purposes</li>
                <li>We do NOT access contacts without your explicit permission</li>
              </ul>
              <p><strong>Your Rights and Control:</strong></p>
              <ul>
                <li>Contacts permission is completely optional - you can add information manually</li>
                <li>You choose which specific contact to import each time</li>
                <li>Only the selected contact's information is used to pre-fill the form</li>
                <li>You can revoke contacts permission at any time through your device settings</li>
                <li>Revoking permission does not affect existing records</li>
              </ul>
              <p><strong>Data Retention:</strong></p>
              <ul>
                <li>Contact information imported becomes part of the applicant record you create</li>
                <li>This data is stored as regular information in our database</li>
                <li>No separate copy of your contacts list is maintained</li>
                <li>All records follow our standard data retention policies (10 years for government records)</li>
              </ul>
              <p><strong>Compliance:</strong></p>
              <ul>
                <li>We request contacts permission following Google Play's User Data policy</li>
                <li>A prominent disclosure is shown before requesting permission</li>
                <li>Permission is requested only when you attempt to use the import feature</li>
                <li>You maintain full control over granting or denying this permission</li>
              </ul>

              <h2 className="title mb-3 mt-5">15. Device Permissions</h2>
              <p>EBARANGAY may request access to various device features to provide its services. All permissions are requested at the time they are needed, and you can manage them through your device settings at any time.</p>
              <p><strong>Camera:</strong></p>
              <ul>
                <li>Purpose: To capture photos of documents, identification, and supporting evidence for barangay service applications</li>
                <li>When requested: When you choose to take a photo for an application or profile</li>
                <li>Control: You can deny or revoke camera permission at any time through device settings</li>
                <li>We do not access the camera in the background or without your explicit action</li>
              </ul>
              <p><strong>Storage (Read/Write External Storage):</strong></p>
              <ul>
                <li>Purpose: To save and access documents, photos, and files for barangay service applications</li>
                <li>When requested: When you choose to upload or download files</li>
                <li>Control: You can deny or revoke storage permission at any time through device settings</li>
                <li>We only access files you explicitly select for upload</li>
              </ul>
              <p><strong>Internet and Network State:</strong></p>
              <ul>
                <li>Purpose: Required for all app functionality including data synchronization, API communication, and real-time updates</li>
                <li>This permission is essential for the app to function and cannot be disabled while using the Service</li>
                <li>All network communication is encrypted via HTTPS/TLS</li>
              </ul>
              <p><strong>Location (Fine, Coarse, and Background):</strong></p>
              <ul>
                <li>Purpose: GPS tracking for authorized barangay personnel (tanod, GPS operators) during duty hours</li>
                <li>Fine Location: Provides precise GPS coordinates for tracking</li>
                <li>Coarse Location: Provides approximate location as a fallback</li>
                <li>Background Location: Allows GPS tracking to continue when the app is in the background (for authorized personnel only)</li>
                <li>When requested: When GPS tracking is activated for authorized personnel</li>
                <li>Control: You can disable location permissions at any time through device settings</li>
                <li>Location data is collected only for authorized personnel during official duty hours</li>
              </ul>
              <p><strong>Contacts:</strong></p>
              <ul>
                <li>Purpose: To simplify data entry by importing contact information for applicant records (see Section 14 for full details)</li>
                <li>When requested: When you choose to import a contact</li>
                <li>Control: Completely optional; you can deny or revoke at any time</li>
              </ul>

              <h2 className="title mb-3 mt-5">16. Data Storage via Supabase</h2>
              <p>EBARANGAY uses Supabase Cloud Storage and Database services to store and manage barangay data.</p>
              <p><strong>Database Storage via Supabase:</strong></p>
              <ul>
                <li>Applicant profiles, barangay clearances, business permits, blotters, incident reports, pet registrations, amicable settlements, events, ordinances, in-app messages, GPS tracking data, audit logs, and other structured data are stored in Supabase PostgreSQL databases</li>
                <li>Data encrypted at rest (AES-256) and in transit (TLS 1.3)</li>
                <li>Regular automated backups</li>
                <li>Retention: 10 years for government records, 30 days for personal data after account deletion (except records required by law)</li>
              </ul>
              <p><strong>Third-Party Service Provider:</strong></p>
              <ul>
                <li><strong>Supabase Inc.</strong> (infrastructure provider)
                  <ul>
                    <li>Website: <a href="https://supabase.com" target="_blank" rel="noopener noreferrer">https://supabase.com</a></li>
                    <li>Privacy Policy: <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">https://supabase.com/privacy</a></li>
                    <li>Location: Global (AWS infrastructure)</li>
                    <li>Compliance: GDPR, SOC 2 Type II, ISO 27001 certified</li>
                    <li>Role: Infrastructure and storage provider only</li>
                    <li>Access: Infrastructure-level access for database operations</li>
                    <li>Use: Does not view, analyze, or use your data for any purpose beyond infrastructure services</li>
                    <li>Sharing: Does not share data with third parties</li>
                  </ul>
                </li>
              </ul>
              <p><strong>Data Transfer:</strong></p>
              <ul>
                <li>Data may be stored on servers outside the Philippines (AWS global infrastructure)</li>
                <li>Transfer is secured via TLS encryption</li>
                <li>Supabase complies with GDPR adequacy requirements for international transfers</li>
                <li>You consent to international transfer when using EBARANGAY</li>
              </ul>

              <h2 className="title mb-3 mt-5">17. Service Providers</h2>
              <p>We may hire third-party companies and individuals to facilitate our Service ("Service Providers"), to offer Service on our behalf, to conduct Service-related functions, or to assist us in analyzing how our Service is utilized.</p>
              <p>Our service providers include:</p>

              <p><strong>a) Supabase Inc. - Database and Backend Services</strong></p>
              <ul>
                <li>Stores all barangay data (clearances, permits, incident reports, blotters, applicant profiles, events, ordinances, messages, GPS data, audit logs, etc.)</li>
                <li>Provides authentication infrastructure and real-time data synchronization</li>
                <li>Data Processing Addendum available</li>
                <li>Website: <a href="https://supabase.com" target="_blank" rel="noopener noreferrer">https://supabase.com</a></li>
                <li>Privacy Policy: <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">https://supabase.com/privacy</a></li>
              </ul>

              <p><strong>b) Mailtrap / Railsware Products, Inc. - Email Delivery Services</strong></p>
              <ul>
                <li>Used to deliver transactional emails including OTP verification codes, password reset emails, and account notifications</li>
                <li>Receives recipient email addresses and email content for delivery purposes</li>
                <li>Does not use your data for marketing or advertising</li>
                <li>Website: <a href="https://mailtrap.io" target="_blank" rel="noopener noreferrer">https://mailtrap.io</a></li>
                <li>Privacy Policy: <a href="https://mailtrap.io/privacy-policy" target="_blank" rel="noopener noreferrer">https://mailtrap.io/privacy-policy</a></li>
              </ul>

              <p><strong>c) Google LLC - Authentication, Maps, and Play Services</strong></p>
              <ul>
                <li>Google Sign-In (OAuth): Used as an authentication option for account creation and login; receives your Google profile information (name, email, profile photo) upon your consent</li>
                <li>Google Maps: Used to display maps for GPS tracking and location-based features; receives location coordinates for map rendering</li>
                <li>Google Play Services: Used for in-app updates and app distribution</li>
                <li>Website: <a href="https://www.google.com" target="_blank" rel="noopener noreferrer">https://www.google.com</a></li>
                <li>Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">https://policies.google.com/privacy</a></li>
              </ul>

              <p><strong>d) IP Geolocation Services - Location Detection</strong></p>
              <ul>
                <li>Services used: ip-api.com, ipapi.co, ipinfo.io</li>
                <li>Purpose: To auto-detect your approximate geographic location for address form pre-filling</li>
                <li>Data shared: Your IP address (automatically included in the network request)</li>
                <li>Data received: Approximate country, region, city, and timezone</li>
                <li>Usage: Transient (not stored separately; used only during your session)</li>
                <li>These services have their own privacy policies:
                  <ul>
                    <li>ip-api.com: <a href="https://ip-api.com/docs/legal" target="_blank" rel="noopener noreferrer">https://ip-api.com/docs/legal</a></li>
                    <li>ipapi.co: <a href="https://ipapi.co/privacy" target="_blank" rel="noopener noreferrer">https://ipapi.co/privacy</a></li>
                    <li>ipinfo.io: <a href="https://ipinfo.io/privacy" target="_blank" rel="noopener noreferrer">https://ipinfo.io/privacy</a></li>
                  </ul>
                </li>
              </ul>

              <p><strong>e) CountriesNow API - Geographic Reference Data</strong></p>
              <ul>
                <li>Purpose: To provide lists of countries, regions, cities, and other geographic data for address forms</li>
                <li>Data shared: No personal data is shared; only geographic lookup queries</li>
                <li>Data received: Lists of geographic names (countries, states, cities)</li>
                <li>Website: <a href="https://countriesnow.space" target="_blank" rel="noopener noreferrer">https://countriesnow.space</a></li>
              </ul>

              <p>These third parties have access to your Personal Data only to carry out these duties on our behalf and are bound to keep it confidential and not to disclose or use it for any other purpose.</p>

              <h2 className="title mb-3 mt-5">18. Analytics</h2>
              <p>Third-party Service Providers may be used to monitor and evaluate how our Service is used to improve barangay service delivery and user experience.</p>

              <h2 className="title mb-3 mt-5">19. Continuous Integration/Continuous Delivery Tools</h2>
              <p>We may utilize third-party Service Providers to automate our Service's development process and ensure secure, reliable updates to the EBARANGAY app.</p>

              <h2 className="title mb-3 mt-5">20. Hypertext Links to Other Websites</h2>
              <p>Our Service may contain links to third-party websites that we do not control. You will be redirected to the third party's website if you click on a third party link. We strongly suggest you read the Privacy Statements of each website that you visit.</p>
              <p>We have no control over the content, privacy policies, or practices of third-party websites and disclaim any responsibility for them.</p>

              <h2 className="title mb-3 mt-5">21. Privacy of Children</h2>
              <p>Children under the age of 18 ("Child" or "Children") are not permitted to use our Services independently.</p>
              <p>We do not collect personally identifying information of children under the age of 18 knowingly without parental consent. Minors may access barangay services through their parents or legal guardians who must create accounts on their behalf.</p>
              <p>Please inform us if you become aware that a child has provided us with Personal Data without parental consent. If we learn that we have obtained Personal Data from children without obtaining parental agreement, we immediately delete the information from our servers.</p>

              <h2 className="title mb-3 mt-5">22. Modifications to This Privacy Statement</h2>
              <p>Our Privacy policy may be updated from time to time. We will notify you of any changes to our Privacy Policy by posting the revised version in the app and via email (if provided).</p>
              <p>Prior to the modification becoming effective, we will notify you through in-app notifications and/or email, and we will revise the "Last Updated" date at the top of this Privacy Policy.</p>
              <p>It is recommended that you examine this Privacy policy periodically for modifications. Changes to this Privacy policy become effective upon their publication.</p>

              <h2 className="title mb-3 mt-5">23. How to Contact Us</h2>
              <p>Please contact us via email at <a href="mailto:support@ebarangay.ph">support@ebarangay.ph</a> if you have any queries regarding this Privacy policy.</p>
              <p>For data privacy concerns, you may also contact:</p>
              <ul>
                <li><strong>National Privacy Commission (NPC)</strong>
                  <ul>
                    <li>Email: <a href="mailto:privacy@privacy.gov.ph">privacy@privacy.gov.ph</a></li>
                    <li>Website: <a href="https://www.privacy.gov.ph" target="_blank" rel="noopener noreferrer">www.privacy.gov.ph</a></li>
                    <li>Hotline: (+63 2) 8234-2228</li>
                  </ul>
                </li>
              </ul>
              <p>For urgent barangay service concerns, please contact your local barangay office directly.</p>

            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;
